import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import {
  ArrowRight,
  Radar,
  Search,
  Workflow,
  Activity,
  Sparkles,
  MessageSquare,
  Scan,
  Filter,
  Quote,
} from "lucide-react";
import { AuroraBackground } from "@/components/aurora-background";
import { GlassOrb } from "@/components/glass-orb";
import { GlassIcon } from "@/components/glass-icon";
import { SectionEyebrow } from "@/components/section-eyebrow";
import { MetricCounters } from "@/components/metric-counters";
import { OrbitLoop } from "@/components/orbit-loop";
import { TestimonialsMasonry } from "@/components/testimonials-masonry";
import { LandingHeader } from "@/components/landing-header";
import { TrustStrip } from "@/components/trust-strip";
import { Input } from "@/components/ui/input";
import { seo } from "@/lib/seo";
import { breadcrumb, webPage } from "@/lib/structured-data";
import { SITE_URL } from "@/lib/seo-constants";
import { signUpUrl, scrollToSection } from "@/lib/cta";

const LP_PATH = "/lp/outcome-marketers";
const LP_TITLE = "Get AI to send buyers your way while you sleep";
const LP_DESCRIPTION =
  "Turn ChatGPT, Perplexity, Claude, and Gemini into your highest-converting organic channel. Axy.digital continuously monitors market signal changes, identifies opportunities and secures high-value brand citations with automated execution.";

export const Route = createFileRoute("/lp/outcome-marketers")({
  head: () =>
    seo({
      title: LP_TITLE,
      description: LP_DESCRIPTION,
      path: LP_PATH,
      type: "website",
      jsonLd: [
        webPage({
          name: LP_TITLE,
          description: LP_DESCRIPTION,
          url: `${SITE_URL}${LP_PATH}`,
        }),
        breadcrumb([
          { name: "Home", url: "/" },
          { name: LP_TITLE, url: LP_PATH },
        ]),
      ],
    }),
  component: LandingPage,
});

const HERO_SIGNUP = signUpUrl("lp-outcome-marketers-hero");
const FINAL_SIGNUP = signUpUrl("lp-outcome-marketers-final");
const HEADER_SIGNUP = signUpUrl("lp-outcome-marketers-header");

const HOW_IT_WORKS_STEPS = [
  {
    label: "The Query",
    description: "Buyers ask ChatGPT, Perplexity, or Gemini for specialized market solutions.",
    icon: MessageSquare,
  },
  {
    label: "The Scan",
    description: "AI engines crawl the web, analyzing data sources and semantic relevance.",
    icon: Scan,
  },
  {
    label: "The Filter",
    description: "Unoptimized structures and generic content are skipped entirely.",
    icon: Filter,
  },
  {
    label: "The Citation",
    description: "Highly structured, data-rich sites claim the exclusive recommendations.",
    icon: Quote,
  },
];

const FINAL_METRICS = [
  { value: "+11×", label: "Citation Growth", proof: "Across 90-Day Rollouts" },
  { value: "4 Engines", label: "LLMs Tracked", proof: "Live API Mapping" },
  { value: "Real-Time", label: "Share-of-Voice", proof: "Vs. Competitor Set" },
  { value: "+40%", label: "Referral Traffic", proof: "In 90 Days Post-Deploy" },
];

function CompetitorAuditForm() {
  const [competitor, setCompetitor] = useState("");
  const [email, setEmail] = useState("");

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const url = new URL(signUpUrl("lp-outcome-marketers-competitor"));
    if (competitor) url.searchParams.set("competitor", competitor);
    if (email) url.searchParams.set("email", email);
    window.location.href = url.toString();
  };

  return (
    <form onSubmit={onSubmit} className="flex flex-col sm:flex-row gap-3 w-full">
      <Input
        type="text"
        required
        placeholder="yourcompetitor.com"
        value={competitor}
        onChange={(e) => setCompetitor(e.target.value)}
        className="h-12 bg-white/5 border-white/15 text-white placeholder:text-white/40 flex-1"
      />
      <Input
        type="email"
        required
        placeholder="you@company.com"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        className="h-12 bg-white/5 border-white/15 text-white placeholder:text-white/40 flex-1"
      />
      <button
        type="submit"
        className="btn-gradient hover:brightness-110 transition-all rounded-full px-6 h-12 text-sm font-medium inline-flex items-center justify-center gap-2 whitespace-nowrap"
      >
        Audit My Competitor's GEO
        <ArrowRight className="size-4" />
      </button>
    </form>
  );
}

function LandingPage() {
  return (
    <>
      <LandingHeader ctaLabel="Get Started" ctaHref={HEADER_SIGNUP} />

      {/* HERO */}
      <section className="relative px-6 pt-20 pb-24 overflow-hidden">
        <AuroraBackground />
        <div
          aria-hidden
          className="absolute left-1/2 top-24 -translate-x-1/2 opacity-60 pointer-events-none"
        >
          <GlassOrb size="lg" />
        </div>
        <div className="relative max-w-4xl mx-auto text-center">
          <SectionEyebrow className="justify-center text-center">
            Turn ChatGPT, Perplexity, Claude &amp; Gemini into your highest-converting organic channel
          </SectionEyebrow>
          <h1 className="text-4xl md:text-6xl lg:text-7xl font-semibold tracking-tight text-white text-balance mb-6">
            Get AI to send buyers your way while you sleep.
          </h1>
          <p className="text-lg md:text-xl text-white/75 max-w-3xl mx-auto mb-10 text-pretty">
            Axy.digital continuously monitors market signal changes, search behaviour, identifies
            opportunities and secures high-value brand citations with automated execution.
          </p>
          <div className="flex flex-col items-center gap-3">
            <a
              href={HERO_SIGNUP}
              className="btn-gradient hover:-translate-y-px hover:brightness-110 transition-all rounded-full px-6 py-3 text-sm md:text-base font-medium inline-flex items-center gap-2"
            >
              Capture Your AI Search Market Share Now (2 Mins)
              <ArrowRight className="size-4" />
            </a>
            <p className="text-xs text-white/55">
              Get started for free • No credit card required
            </p>
          </div>
        </div>
      </section>

      <TrustStrip />

      {/* METRICS BANNER */}
      <section className="px-6 py-20">
        <div className="max-w-6xl mx-auto">
          <SectionEyebrow>Show up in AI search on autopilot</SectionEyebrow>
          <h2 className="text-3xl md:text-4xl font-semibold tracking-tight text-white text-balance mb-3 max-w-3xl">
            Track every prompt, every model, every competitor — and automatically generate GEO
            campaigns that help you show up.
          </h2>
          <p className="text-white/65 max-w-2xl mb-10">
            Built for lean startup teams that need to automate their brand being found, cited, and
            recommended by AI search engines.
          </p>
          <MetricCounters
            items={[
              {
                value: 11,
                suffix: "×",
                label: "Citation growth",
                proof: "Lift across 90-day rollouts",
              },
              {
                value: 4,
                label: "LLMs tracked live",
                proof: "ChatGPT · Perplexity · Claude · Gemini",
              },
              {
                value: 100,
                suffix: "%",
                label: "Share-of-voice mapping",
                proof: "Real-time vs. your competitor set",
              },
              {
                value: 40,
                prefix: "+",
                suffix: "%",
                label: "LLM referral traffic",
                proof: "Over 90 days post-deployment",
              },
            ]}
          />
        </div>
      </section>

      {/* HOW AI SEARCH WORKS */}
      <section className="relative px-6 py-24 overflow-hidden">
        <div
          aria-hidden
          className="absolute inset-0 opacity-60"
          style={{
            background:
              "radial-gradient(60% 40% at 30% 20%, rgba(123,63,242,0.18), transparent 60%)",
          }}
        />
        <div className="relative max-w-6xl mx-auto">
          <SectionEyebrow>How AI search works</SectionEyebrow>
          <h2 className="text-3xl md:text-5xl font-semibold tracking-tight text-white text-balance mb-6 max-w-3xl">
            Traditional search engine parameters are shifting.
          </h2>
          <p className="text-white/75 leading-relaxed text-pretty max-w-3xl mb-12">
            Conversational answer engines use a strict engineering filter to choose product
            recommendations.
          </p>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {HOW_IT_WORKS_STEPS.map((step, i) => (
              <div
                key={step.label}
                className="glass-card p-6 hover-lift hover:-translate-y-1 relative"
              >
                <div className="flex items-center justify-between mb-4">
                  <GlassIcon icon={step.icon} />
                  <span className="text-xs uppercase tracking-wider text-white/40 tabular-nums">
                    0{i + 1}
                  </span>
                </div>
                <h3 className="text-base font-semibold text-white mb-2">{step.label}</h3>
                <p className="text-sm text-white/65 leading-relaxed">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SHOW UP IN AI SEARCH (ORBIT LOOP) */}
      <section className="px-6 py-24">
        <div className="max-w-6xl mx-auto text-center mb-12">
          <SectionEyebrow className="justify-center">The Axy.digital engine</SectionEyebrow>
          <h2 className="text-3xl md:text-5xl font-semibold tracking-tight text-white text-balance max-w-3xl mx-auto mb-4">
            Show up in AI search in one click.
          </h2>
          <p className="text-white/70 max-w-2xl mx-auto">
            Axy.digital bridges the gap between market intelligence and programmatic execution, automating
            your entire visibility pipeline across 5 distinct phases.
          </p>
        </div>
        <div className="max-w-5xl mx-auto">
          <OrbitLoop
            steps={[
              {
                label: "Map Intent",
                description:
                  "Axy.digital maps real-time conversational behavior and surfaces the exact strategic prompts your buyers are actively typing into AI engines.",
                icon: Radar,
              },
              {
                label: "Analyze Gaps",
                description:
                  "Instantly uncover exactly where your brand is included, where you are getting skipped, and which competitors are winning your category share-of-voice.",
                icon: Search,
              },
              {
                label: "Programmatic Execution",
                description:
                  "Automatically deploy the precise content hubs and technical data schemas required to establish source authority and show up in AI search.",
                icon: Workflow,
              },
              {
                label: "Continuous Tracking",
                description:
                  "Maintain absolute clarity over your growth with historical performance tracking showing your citation footprint scale over time.",
                icon: Activity,
              },
              {
                label: "Learning & Optimization",
                description:
                  "Run an always-on infrastructure layer that adapts dynamically based on performance data, weekly search trends, and model updates.",
                icon: Sparkles,
              },
            ]}
          />
          <div className="mt-8 flex justify-center">
            <span className="glass-pill px-4 py-2 text-xs text-white/85 inline-flex items-center gap-2">
              <span className="size-1.5 rounded-full bg-pink-soft animate-pulse" />0 manual steps ·
              Loop running on autopilot
            </span>
          </div>
        </div>
      </section>

      {/* COMPETITOR AUDIT — SECONDARY CTA */}
      <section id="competitor-audit-zone" className="px-6 py-20 scroll-mt-20">
        <div className="max-w-4xl mx-auto">
          <div className="glass-card p-8 md:p-12">
            <SectionEyebrow>Competitor intelligence</SectionEyebrow>
            <h2 className="text-3xl md:text-4xl font-semibold tracking-tight text-white text-balance mb-4">
              Want to see who AI is recommending instead of you?
            </h2>
            <p className="text-white/70 max-w-2xl mb-8 text-pretty">
              Stop guessing what your rivals are doing. Enter your competitor's domain below, and
              Axy.digital will reverse-engineer how your competitors are locking down citations,
              manipulating AI model data-feeds, and capturing your traffic.
            </p>
            <CompetitorAuditForm />
            <p className="mt-4 text-xs text-white/50">
              We'll email your competitor GEO audit within minutes.
            </p>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <TestimonialsMasonry
        eyebrow="Founder social proof"
        heading="Founders & Marketers winning AI search with Axy.digital."
        items={[
          {
            quote:
              "Axy.digital helped us 5x our content output without sounding generic. The content is detailed, sharp, and actually reflects how we want to show up as an early-stage team.",
            name: "Aurelien Vasinis",
            role: "Founder & CEO",
            company: "Kuration AI",
            initials: "AV",
            hue: 295,
            metric: { value: "60+ hrs", label: "saved monthly" },
            featured: true,
          },
          {
            quote:
              "In 90 days, Axy.digital helped us go from invisible in AI answers to ranking #3 against incumbents with a decade of SEO dominance.",
            name: "Savannah Thatcher",
            role: "CMO",
            company: "Wingbits",
            initials: "ST",
            hue: 320,
            metric: { value: "11×", label: "AI visibility growth" },
          },
          {
            quote:
              "The depth of the content Axy.digital generated from very little input was genuinely impressive. Not having to prompt or configure everything manually was a game changer.",
            name: "Florian Rennberger",
            role: "Head of Sales",
            company: "Conducttr Germany",
            initials: "FR",
            hue: 270,
          },
        ]}
      />

      {/* FINAL CTA */}
      <section className="relative px-6 py-24 border-t border-white/10 bg-neutral-950 overflow-hidden">
        <div className="relative max-w-5xl mx-auto text-center">
          {/* SYSTEM ACTIVE pulse */}
          <div className="flex justify-center mb-8">
            <span className="inline-flex items-center gap-2.5 px-3.5 py-1.5 rounded-full border border-emerald-400/25 bg-emerald-400/5 text-[11px] uppercase tracking-[0.18em] text-emerald-300/90">
              <span className="relative flex size-2">
                <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75 animate-ping" />
                <span className="relative inline-flex size-2 rounded-full bg-emerald-400" />
              </span>
              System Active
            </span>
          </div>

          <h2 className="text-4xl md:text-5xl lg:text-6xl font-semibold tracking-tight text-white text-balance mb-5">
            Stop Being Invisible to the Engines Driving 2026 B2B Revenue.
          </h2>
          <p className="text-lg text-white/70 max-w-2xl mx-auto mb-12 text-pretty">
            Your market is actively migrating away from traditional discovery and evaluation of
            vendors. Every hour you remain unoptimized for conversational answer engines, your
            competitors are locking down permanent citation weights. Deploy your autonomous
            visibility layer today.
          </p>

          {/* Metrics border-grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 border border-white/10 rounded-2xl overflow-hidden mb-12 bg-white/[0.02]">
            {FINAL_METRICS.map((m, i) => (
              <div
                key={m.label}
                className={[
                  "px-5 py-6 text-left",
                  i % 2 === 1 ? "" : "border-r border-white/10",
                  "md:border-r",
                  i === FINAL_METRICS.length - 1 ? "md:border-r-0" : "",
                  i < 2 ? "border-b border-white/10 md:border-b-0" : "",
                ].join(" ")}
              >
                <div className="text-xs uppercase tracking-wider text-white/45 mb-2">
                  {m.label}
                </div>
                <div className="text-2xl md:text-3xl font-semibold text-white mb-1 tabular-nums">
                  {m.value}
                </div>
                <div className="text-xs text-white/50">{m.proof}</div>
              </div>
            ))}
          </div>

          <div className="flex flex-col items-center gap-3">
            <a
              href={FINAL_SIGNUP}
              className="btn-gradient hover:-translate-y-px hover:brightness-110 transition-all rounded-full px-7 py-3.5 text-sm md:text-base font-medium inline-flex items-center gap-2"
            >
              Claim Your AI Search Footprint (Free · 2 Mins)
              <ArrowRight className="size-4" />
            </a>
            <p className="text-xs text-white/55">
              No credit card required. Includes a free AI visibility report and real-time signal
              maps.
            </p>
          </div>

          <div className="mt-10 text-sm text-white/55">
            Prefer to see the competitor's strategy first?{" "}
            <button
              type="button"
              onClick={() => scrollToSection("competitor-audit-zone")}
              className="text-white/85 underline underline-offset-4 hover:text-white transition-colors"
            >
              Run Competitor GEO Audit
            </button>
          </div>
        </div>
      </section>
    </>
  );
}
